import React from 'react';
import Select from 'react-select';
import AvailabilityPlannerService from '../../../Services/AvailabilityPlannerService';
import OfficeService from '../../../Services/OfficeService';
import AuthService from '../../../Services/authService';
import ErrorMessage from '../../../Elements/ErrorMessage';
import Loading from '../../../Elements/loading';
import FaIcon from '../../../Elements/FaIcon';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import { LOCATION, ABSENCE } from './availabilityTaxonomy';

// A day is 8 hours; the backend returns hours per category and the UI shows fractional days (hours / 8).
const STANDARD_DAY_HOURS = 8;

// Columns: work locations first, then absence types. `key` is the category code the backend sums under.
const COLUMNS = [
  ...Object.entries(LOCATION).map(([key, meta]) => ({ key, group: 'work', ...meta })),
  ...Object.entries(ABSENCE).map(([key, meta]) => ({ key, group: 'absent', ...meta })),
];

// Availability Planner statistics: per-employee days per category over a month or year, for the caller's
// team or an office selection. Read-only.
export default class AvailabilityPlannerStats extends React.Component {
  constructor(props) {
    super(props);
    const loggedInUser = AuthService.getInstance().GetLoggedInUser();
    const defaultOffice = (loggedInUser && loggedInUser.officeId) || 0;
    this.state = {
      scope: 'team', // 'team' | 'office'
      period: 'month', // 'month' | 'year'
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      offices: [],
      officeIds: defaultOffice ? [defaultOffice] : [],
      selectedDepartments: [],
      rows: [],
      loading: true,
    };
    // Per-user debounce timers for the self-saving allowance edit (not state — they don't affect render).
    this.allowanceSaveTimers = {};
  }

  componentWillUnmount() {
    Object.values(this.allowanceSaveTimers).forEach((t) => clearTimeout(t));
  }

  componentDidMount() {
    OfficeService.GetOfficesForSelect()
      .then((offices) => this.setState({ offices: Array.isArray(offices) ? offices : [] }))
      .catch(() => ErrorMessage.SetErrorMessage('APS28'));
    this.load();
  }

  pad(n) {
    return String(n).padStart(2, '0');
  }

  // yyyy-MM-dd range (ToDate exclusive) for the selected period, built from local parts (no timezone shift).
  dateRange() {
    const { period, year, month } = this.state;
    if (period === 'year') {
      return { from: `${year}-01-01`, to: `${year + 1}-01-01` };
    }
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    return { from: `${year}-${this.pad(month)}-01`, to: `${nextYear}-${this.pad(nextMonth)}-01` };
  }

  load() {
    this.setState({ loading: true });
    const { from, to } = this.dateRange();
    const officeScope = this.state.scope === 'office';
    AvailabilityPlannerService.GetStats(officeScope, this.state.officeIds, from, to)
      .then((data) => this.setState({ rows: Array.isArray(data) ? data : [] }))
      .catch(() => ErrorMessage.SetErrorMessage('APS27'))
      .finally(() => this.setState({ loading: false }));
  }

  changeMonth(delta) {
    let month = this.state.month + delta;
    let year = this.state.year;
    if (month < 1) {
      month = 12;
      year -= 1;
    } else if (month > 12) {
      month = 1;
      year += 1;
    }
    this.setState({ month, year }, () => this.load());
  }

  changeYear(delta) {
    this.setState({ year: this.state.year + delta }, () => this.load());
  }

  setScope(scope) {
    if (scope !== this.state.scope) {
      this.setState({ scope }, () => this.load());
    }
  }

  setPeriod(period) {
    if (period !== this.state.period) {
      this.setState({ period }, () => this.load());
    }
  }

  changeOffices(officeIds) {
    this.setState({ officeIds: Array.isArray(officeIds) ? officeIds : [] }, () => this.load());
  }

  officeOptions() {
    return this.state.offices.map((o) => ({ value: o.officeId, label: o.name }));
  }

  departmentOptions() {
    const set = new Set();
    this.state.rows.forEach((r) => set.add(r.departmentName || 'Unassigned'));
    return [...set].sort((a, b) => a.localeCompare(b)).map((d) => ({ value: d, label: d }));
  }

  // Department filter (office scope only) + order by department, then last, then first name.
  visibleRows() {
    let rows = this.state.rows;
    if (this.state.scope === 'office' && this.state.selectedDepartments.length > 0) {
      const selected = new Set(this.state.selectedDepartments);
      rows = rows.filter((r) => selected.has(r.departmentName || 'Unassigned'));
    }
    return [...rows].sort(
      (a, b) =>
        (a.departmentName || 'Unassigned').localeCompare(b.departmentName || 'Unassigned') ||
        (a.lastName || '').localeCompare(b.lastName || '') ||
        (a.firstName || '').localeCompare(b.firstName || '') ||
        (a.name || '').localeCompare(b.name || '')
    );
  }

  // Hours -> days; blank for zero so the table stays readable.
  fmtDays(hours) {
    const days = (Number(hours) || 0) / STANDARD_DAY_HOURS;
    return days ? days.toFixed(1) : '';
  }

  rowTotalHours(row) {
    return Object.values(row.hours || {}).reduce((sum, h) => sum + (Number(h) || 0), 0);
  }

  // Compact number: integers without decimals, otherwise one decimal; blank for null/undefined.
  numFmt(value) {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const n = Number(value);
    if (Number.isNaN(n)) {
      return '';
    }
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  }

  // Vacation split: Planned = self-planned + BambooHR-requested (pending); approved days are split by date
  // into Taken = past incl. today (already used) and Approved = future excl. today (confirmed, not yet taken).
  plannedVacationDays(row) {
    return (Number(row.vacationPlannedHours) || 0) / STANDARD_DAY_HOURS;
  }

  takenVacationDays(row) {
    return (Number(row.vacationTakenHours) || 0) / STANDARD_DAY_HOURS;
  }

  approvedVacationDays(row) {
    return (Number(row.vacationApprovedHours) || 0) / STANDARD_DAY_HOURS;
  }

  // Total vacation used = planned + taken + approved (all count against the allowance).
  vacationDays(row) {
    return this.plannedVacationDays(row) + this.takenVacationDays(row) + this.approvedVacationDays(row);
  }

  // Remaining vacation = yearly allowance - vacation days used (planned + taken + approved). Blank when no allowance set.
  remaining(row) {
    if (row.allowedVacationDays === null || row.allowedVacationDays === undefined) {
      return null;
    }
    return Number(row.allowedVacationDays) - this.vacationDays(row);
  }

  // Debounced self-save: fires 2s after the user stops typing in an allowance cell. The year is captured
  // now so navigating years mid-debounce can't save against the wrong year.
  scheduleSaveAllowance(row, rawValue) {
    const year = this.state.year;
    if (this.allowanceSaveTimers[row.userId]) {
      clearTimeout(this.allowanceSaveTimers[row.userId]);
    }
    this.allowanceSaveTimers[row.userId] = setTimeout(() => {
      delete this.allowanceSaveTimers[row.userId];
      this.saveAllowance(row, rawValue, year);
    }, 2000);
  }

  // Persists the allowance for the given year, then updates the row in place.
  saveAllowance(row, rawValue, year) {
    const parsed = rawValue === '' ? 0 : Number(rawValue);
    if (Number.isNaN(parsed) || parsed < 0) {
      return;
    }
    const current =
      row.allowedVacationDays === null || row.allowedVacationDays === undefined
        ? null
        : Number(row.allowedVacationDays);
    if (current === parsed) {
      return;
    }
    AvailabilityPlannerService.SaveVacationAllowance(row.userId, year, parsed)
      .then((res) => {
        if (res && (res.error || res.Error)) {
          ErrorMessage.SetErrorMessage(res.error || res.Error);
          return;
        }
        this.setState((s) => ({
          rows: s.rows.map((r) => (r.userId === row.userId ? { ...r, allowedVacationDays: parsed } : r)),
        }));
      })
      .catch(() => ErrorMessage.SetErrorMessage('APS31'));
  }

  // Downloads the currently displayed statistics (respecting scope, period and filters) as a CSV.
  exportCsv() {
    const rows = this.visibleRows();
    if (rows.length === 0) {
      return;
    }
    const officeScope = this.state.scope === 'office';
    const isYear = this.state.period === 'year';
    const inlineColumns = isYear ? COLUMNS.filter((c) => c.key !== 'vacation') : COLUMNS;
    const dayVal = (hours) => Math.round(((Number(hours) || 0) / STANDARD_DAY_HOURS) * 100) / 100;

    const header = ['Employee'];
    if (officeScope) {
      header.push('Department');
    }
    if (isYear) {
      header.push('Vacation allowed', 'Vacation planned', 'Vacation taken', 'Vacation approved', 'Vacation remaining');
    }
    inlineColumns.forEach((c) => header.push(c.label));
    header.push('Total');

    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const lines = [header];
    rows.forEach((r) => {
      const line = [r.name];
      if (officeScope) {
        line.push(r.departmentName || 'Unassigned');
      }
      if (isYear) {
        line.push(r.allowedVacationDays ?? '');
        line.push(round2(this.plannedVacationDays(r)));
        line.push(round2(this.takenVacationDays(r)));
        line.push(round2(this.approvedVacationDays(r)));
        const rem = this.remaining(r);
        line.push(rem === null ? '' : round2(rem));
      }
      inlineColumns.forEach((c) => line.push(dayVal((r.hours || {})[c.key])));
      line.push(dayVal(this.rowTotalHours(r)));
      lines.push(line);
    });

    const csv = lines.map((cols) => cols.map((v) => csvCell(v)).join(',')).join('\r\n');
    const period = isYear ? `${this.state.year}` : `${this.state.year}-${this.pad(this.state.month)}`;
    downloadCsv(`availability-stats-${officeScope ? 'office' : 'team'}-${period}.csv`, csv);
  }

  renderControls() {
    const officeScope = this.state.scope === 'office';
    const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };
    // The menus go on the page with menuPosition='fixed', as in MultiChoice: an 'absolute' one can loop with the page
    // scroll (CD-5713).
    const menuPortalTarget = typeof document !== 'undefined' ? document.body : null;
    const officeOptions = this.officeOptions();
    const officeValue = officeOptions.filter((o) => this.state.officeIds.includes(o.value));
    const deptOptions = this.departmentOptions();
    const deptValue = deptOptions.filter((d) => this.state.selectedDepartments.includes(d.value));
    return (
      <>
        <div className='row p-1 align-items-center'>
          <div className='col-auto'>
            <div
              className='btn-group btn-group-sm'
              role='group'
            >
              <button
                type='button'
                className={`btn ${this.state.scope === 'team' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => this.setScope('team')}
              >
                My team
              </button>
              <button
                type='button'
                className={`btn ${officeScope ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => this.setScope('office')}
              >
                Office-wide
              </button>
            </div>
          </div>
          <div className='col-auto'>
            <div
              className='btn-group btn-group-sm'
              role='group'
            >
              <button
                type='button'
                className={`btn ${this.state.period === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => this.setPeriod('month')}
              >
                Month
              </button>
              <button
                type='button'
                className={`btn ${this.state.period === 'year' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => this.setPeriod('year')}
              >
                Year
              </button>
            </div>
          </div>
          <div className='col-auto d-flex align-items-center h6 mb-0'>
            {this.state.period === 'year' ? (
              <>
                <FaIcon
                  iconName='fa fa-chevron-left'
                  tooltipText='Previous year'
                  onClick={() => this.changeYear(-1)}
                />
                <span className='mx-3'>{this.state.year}</span>
                <FaIcon
                  iconName='fa fa-chevron-right'
                  tooltipText='Next year'
                  onClick={() => this.changeYear(1)}
                />
              </>
            ) : (
              <>
                <FaIcon
                  iconName='fa fa-chevron-left'
                  tooltipText='Previous month'
                  onClick={() => this.changeMonth(-1)}
                />
                <span className='mx-3'>
                  {dateTimeHelper.MonthName(this.state.month - 1)} {this.state.year}
                </span>
                <FaIcon
                  iconName='fa fa-chevron-right'
                  tooltipText='Next month'
                  onClick={() => this.changeMonth(1)}
                />
              </>
            )}
          </div>
          <div className='col-auto ms-auto'>
            <button
              type='button'
              className='btn btn-sm btn-outline-secondary'
              onClick={() => this.exportCsv()}
              disabled={this.state.rows.length === 0}
            >
              <i className='fa-solid fa-file-csv me-1' />
              Export CSV
            </button>
          </div>
        </div>
        {officeScope && (
          <div className='row p-1'>
            <div className='col-md-6'>
              <div className='ap-filter-label'>Offices (none = all)</div>
              <Select
                isMulti
                closeMenuOnSelect={false}
                options={officeOptions}
                value={officeValue}
                onChange={(sel) => this.changeOffices((sel || []).map((o) => o.value))}
                placeholder='All offices'
                menuPortalTarget={menuPortalTarget}
                menuPosition='fixed'
                styles={selectStyles}
              />
            </div>
            <div className='col-md-6'>
              <div className='ap-filter-label'>Departments (none = all)</div>
              <Select
                isMulti
                closeMenuOnSelect={false}
                options={deptOptions}
                value={deptValue}
                onChange={(sel) => this.setState({ selectedDepartments: (sel || []).map((d) => d.value) })}
                placeholder='All departments'
                menuPortalTarget={menuPortalTarget}
                menuPosition='fixed'
                styles={selectStyles}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  renderAllowanceValue(row) {
    if (row.canEditAllowance) {
      return (
        <input
          key={`allow-${row.userId}-${this.state.year}`}
          type='number'
          className='ap-allowance-input'
          min='0'
          step='0.5'
          defaultValue={row.allowedVacationDays ?? ''}
          onChange={(e) => this.scheduleSaveAllowance(row, e.target.value)}
        />
      );
    }
    return this.numFmt(row.allowedVacationDays);
  }

  // Cell class for a column inside a group block: the group's background tint, plus a left border on the
  // first column and a right border on the last, so the block reads as one bordered unit.
  groupClass(bg, index, length) {
    let cls = `${bg} text-end`;
    if (index === 0) {
      cls += ' ap-grp-l';
    }
    if (index === length - 1) {
      cls += ' ap-grp-r';
    }
    return cls;
  }

  renderTable() {
    const rows = this.visibleRows();
    const officeScope = this.state.scope === 'office';
    const isYear = this.state.period === 'year';
    if (rows.length === 0) {
      return <div className='ap-empty p-3'>No data for this selection.</div>;
    }
    // In Year view the columns are grouped into three distinct blocks: Vacation (Allowed/Planned/Remaining),
    // Work locations, and Other absences. In Month view everything stays a single flat set of columns.
    const inlineColumns = isYear ? COLUMNS.filter((c) => c.key !== 'vacation') : COLUMNS;
    const workColumns = COLUMNS.filter((c) => c.group === 'work');
    const otherAbsenceColumns = COLUMNS.filter((c) => c.group === 'absent' && c.key !== 'vacation');
    const totals = {};
    inlineColumns.forEach((c) => {
      totals[c.key] = 0;
    });
    let grandTotal = 0;
    let plannedVacTotal = 0;
    let takenVacTotal = 0;
    let approvedVacTotal = 0;
    let allowedTotal = 0;
    let remainingTotal = 0;
    let anyAllowance = false;
    rows.forEach((r) => {
      COLUMNS.forEach((c) => {
        grandTotal += Number((r.hours || {})[c.key]) || 0;
      });
      inlineColumns.forEach((c) => {
        totals[c.key] += Number((r.hours || {})[c.key]) || 0;
      });
      if (isYear) {
        plannedVacTotal += Number(r.vacationPlannedHours) || 0;
        takenVacTotal += Number(r.vacationTakenHours) || 0;
        approvedVacTotal += Number(r.vacationApprovedHours) || 0;
        if (r.allowedVacationDays !== null && r.allowedVacationDays !== undefined) {
          anyAllowance = true;
          allowedTotal += Number(r.allowedVacationDays);
          remainingTotal += Number(r.allowedVacationDays) - this.vacationDays(r);
        }
      }
    });
    return (
      <div className='table-responsive'>
        <table className='table table-sm table-hover ap-stats-table'>
          <thead>
            {isYear ? (
              <>
                <tr>
                  <th rowSpan={2}>Employee</th>
                  {officeScope && <th rowSpan={2}>Department</th>}
                  <th
                    colSpan={5}
                    className='ap-grp-head ap-bg-vac ap-grp-l ap-grp-r'
                  >
                    Vacation
                  </th>
                  <th
                    colSpan={workColumns.length}
                    className='ap-grp-head ap-bg-work ap-grp-l ap-grp-r'
                  >
                    Work locations
                  </th>
                  <th
                    colSpan={otherAbsenceColumns.length}
                    className='ap-grp-head ap-bg-abs ap-grp-l ap-grp-r'
                  >
                    Other absences
                  </th>
                  <th
                    rowSpan={2}
                    className='ap-total-head'
                  >
                    Total
                  </th>
                </tr>
                <tr>
                  <th className='ap-bg-vac ap-grp-l text-end'>Allowed</th>
                  <th className='ap-bg-vac text-end'>Planned</th>
                  <th className='ap-bg-vac text-end'>Taken</th>
                  <th className='ap-bg-vac text-end'>Approved</th>
                  <th className='ap-bg-vac ap-grp-r text-end'>Remaining</th>
                  {workColumns.map((c, i) => (
                    <th
                      key={c.key}
                      className={this.groupClass('ap-bg-work', i, workColumns.length)}
                      title={c.label}
                    >
                      <i className={`fa-solid ${c.icon} me-1`} />
                      {c.label}
                    </th>
                  ))}
                  {otherAbsenceColumns.map((c, i) => (
                    <th
                      key={c.key}
                      className={this.groupClass('ap-bg-abs', i, otherAbsenceColumns.length)}
                      title={c.label}
                    >
                      <i className={`fa-solid ${c.icon} me-1`} />
                      {c.label}
                    </th>
                  ))}
                </tr>
              </>
            ) : (
              <tr>
                <th>Employee</th>
                {officeScope && <th>Department</th>}
                {inlineColumns.map((c) => (
                  <th
                    key={c.key}
                    className='text-end'
                    title={c.label}
                  >
                    <i className={`fa-solid ${c.icon} me-1`} />
                    {c.label}
                  </th>
                ))}
                <th className='ap-total-head'>Total</th>
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.userId}>
                <td className='ap-stats-name'>{r.name}</td>
                {officeScope && <td className='text-muted'>{r.departmentName || 'Unassigned'}</td>}
                {isYear ? (
                  <>
                    <td className='ap-bg-vac ap-grp-l text-end'>{this.renderAllowanceValue(r)}</td>
                    <td className='ap-bg-vac text-end'>{this.fmtDays(r.vacationPlannedHours)}</td>
                    <td className='ap-bg-vac text-end'>{this.fmtDays(r.vacationTakenHours)}</td>
                    <td className='ap-bg-vac text-end'>{this.fmtDays(r.vacationApprovedHours)}</td>
                    <td className='ap-bg-vac ap-grp-r text-end'>{this.numFmt(this.remaining(r))}</td>
                    {workColumns.map((c, i) => (
                      <td
                        key={c.key}
                        className={this.groupClass('ap-bg-work', i, workColumns.length)}
                      >
                        {this.fmtDays((r.hours || {})[c.key])}
                      </td>
                    ))}
                    {otherAbsenceColumns.map((c, i) => (
                      <td
                        key={c.key}
                        className={this.groupClass('ap-bg-abs', i, otherAbsenceColumns.length)}
                      >
                        {this.fmtDays((r.hours || {})[c.key])}
                      </td>
                    ))}
                  </>
                ) : (
                  inlineColumns.map((c) => (
                    <td
                      key={c.key}
                      className='text-end'
                    >
                      {this.fmtDays((r.hours || {})[c.key])}
                    </td>
                  ))
                )}
                <td className='text-end fw-bold'>{this.fmtDays(this.rowTotalHours(r))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className='ap-stats-total fw-bold'>
              <td>Total</td>
              {officeScope && <td />}
              {isYear ? (
                <>
                  <td className='ap-bg-vac ap-grp-l text-end'>{anyAllowance ? this.numFmt(allowedTotal) : ''}</td>
                  <td className='ap-bg-vac text-end'>{this.fmtDays(plannedVacTotal)}</td>
                  <td className='ap-bg-vac text-end'>{this.fmtDays(takenVacTotal)}</td>
                  <td className='ap-bg-vac text-end'>{this.fmtDays(approvedVacTotal)}</td>
                  <td className='ap-bg-vac ap-grp-r text-end'>{anyAllowance ? this.numFmt(remainingTotal) : ''}</td>
                  {workColumns.map((c, i) => (
                    <td
                      key={c.key}
                      className={this.groupClass('ap-bg-work', i, workColumns.length)}
                    >
                      {this.fmtDays(totals[c.key])}
                    </td>
                  ))}
                  {otherAbsenceColumns.map((c, i) => (
                    <td
                      key={c.key}
                      className={this.groupClass('ap-bg-abs', i, otherAbsenceColumns.length)}
                    >
                      {this.fmtDays(totals[c.key])}
                    </td>
                  ))}
                </>
              ) : (
                inlineColumns.map((c) => (
                  <td
                    key={c.key}
                    className='text-end'
                  >
                    {this.fmtDays(totals[c.key])}
                  </td>
                ))
              )}
              <td className='text-end'>{this.fmtDays(grandTotal)}</td>
            </tr>
          </tfoot>
        </table>
        <div className='text-muted small px-1'>
          Values are days (8 hours = 1 day). Part-days count fractionally.
          {isYear
            ? ' Columns are grouped into Vacation (Allowed = yearly entitlement; Planned = planned +' +
              ' requested days; Taken = approved days in the past incl. today; Approved = approved days in' +
              ' the future; Remaining = Allowed − Planned − Taken − Approved), Work locations, and Other' +
              ' absences.'
            : ''}
        </div>
      </div>
    );
  }

  render() {
    return (
      <div>
        {this.renderControls()}
        {this.state.loading ? <Loading /> : this.renderTable()}
      </div>
    );
  }
}

// Quote a CSV cell when it contains a comma, quote or newline (doubling embedded quotes).
function csvCell(value) {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Trigger a client-side download of the CSV text (UTF-8 BOM so Excel reads accents correctly).
function downloadCsv(filename, csv) {
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
